import { useState, CSSProperties } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  FilterOptionsState,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { cnpj } from "cpf-cnpj-validator";
import colors from "../../styles/colors";
import { useCustomers } from "../../hooks/queries/useCustomers";
import { useCustomerLogin } from "../../hooks/mutations/useCustomerLogin";
import { getClientData } from "../../services/auth";
import { Logout } from "../Logout";

interface ParsedPart {
  text: string;
  highlight: boolean;
}

interface Customer {
  cd_cliente: number;
  nr_dv_cliente: number;
  cnpj: number;
  ds_cliente: string;
}

interface CustomerSelectModalProps {
  open: boolean;
  handleClose: () => void;
  isLogin?: boolean;
}

function rand() {
  return Math.round(Math.random() * 20) - 10;
}

function getModalStyle(): CSSProperties {
  const top = 50 + rand();
  const left = 50 + rand();

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    position: "relative",
  };
}

const CustomerSelectModal = ({
  open,
  handleClose,
  isLogin = false,
}: CustomerSelectModalProps) => {
  const { data: customers = [], isLoading: isLoadingCustomers } =
    useCustomers(open);
  const customerLoginMutation = useCustomerLogin();
  const [modalStyle] = useState(getModalStyle);
  const [value, setValue] = useState<Customer | undefined>(undefined);
  const [searchValue, setSearchValue] = useState("");
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const onClose = (_event: unknown, reason?: string) => {
    if (reason) return;
    handleClose();
  };

  const handleSelect = () => {
    if (!value) return;
    customerLoginMutation.mutate({ cd_cliente: value.cd_cliente });
  };

  const filterOptions = (
    options: Customer[],
    state: FilterOptionsState<Customer>
  ) => {
    if (state.inputValue === "") {
      return [];
    }

    const defaultFilter = createFilterOptions({
      stringify: (option: Customer) => {
        const rawCnpj = option.cnpj.toString().padStart(14, "0");
        const formattedCnpj = cnpj.format(rawCnpj);

        return `${option.cd_cliente}${option.nr_dv_cliente} ${rawCnpj} ${formattedCnpj} ${option.ds_cliente}`;
      },
    });

    return defaultFilter(options, state).slice(0, 10);
  };

  // Abre o dropdown quando há texto digitado
  const handleInputChange = (_event: unknown, newSearchValue: string) => {
    setSearchValue(newSearchValue);
    setAutocompleteOpen(newSearchValue.length > 0);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        style={modalStyle}
        sx={{
          position: "absolute",
          width: 500,
          backgroundColor: "background.paper",
          border: `2px solid ${colors.border.black}`,
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6">Seleção de cliente</Typography>
        <Typography variant="body1">
          Digite o código do cliente Gam ou CNPJ ou Razão Social
        </Typography>
        <Autocomplete<Customer>
          popupIcon={null}
          value={value ?? null}
          onChange={(_, newValue) => setValue(newValue ?? undefined)}
          inputValue={searchValue}
          onInputChange={handleInputChange}
          options={customers}
          getOptionLabel={option => option.ds_cliente}
          filterOptions={filterOptions}
          fullWidth
          autoHighlight
          clearOnEscape
          openOnFocus={false}
          open={autocompleteOpen}
          onOpen={() => setAutocompleteOpen(true)}
          onClose={() => setAutocompleteOpen(false)}
          disabled={customerLoginMutation.isPending || isLoadingCustomers}
          loading={isLoadingCustomers}
          noOptionsText={
            isLoadingCustomers
              ? "Carregando clientes..."
              : searchValue
                ? "Nenhum cliente encontrado"
                : "Digite para buscar"
          }
          renderInput={params => {
            const { InputLabelProps, ...restParams } = params;
            const inputLabelProps = InputLabelProps
              ? {
                  ...InputLabelProps,
                  className: InputLabelProps.className ?? "",
                }
              : undefined;

            return (
              <TextField
                {...restParams}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingCustomers ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                {...(inputLabelProps && {
                  InputLabelProps: inputLabelProps as {
                    className: string;
                    [key: string]: unknown;
                  },
                })}
              />
            );
          }}
          renderOption={(props, option, state) => {
            const { inputValue } = state;
            const nameMatches = match(option.ds_cliente, inputValue);
            const nameParts = parse(option.ds_cliente, nameMatches);

            const code = `${option.cd_cliente}${option.nr_dv_cliente}`;
            const codeMatches = match(code, inputValue);
            const codeParts = parse(code, codeMatches);

            const rawCnpj = option.cnpj.toString().padStart(14, "0");
            const formattedCnpj = cnpj.format(rawCnpj);
            const cnpjMatches = match(formattedCnpj, inputValue);
            const cnpjParts = parse(formattedCnpj, cnpjMatches);

            return (
              <li {...props} key={option.cd_cliente}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div>
                    {nameParts.map((part: ParsedPart, index: number) => (
                      <span
                        key={index}
                        style={{ fontWeight: part.highlight ? 700 : 400 }}
                      >
                        {part.text}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: colors.text.secondary }}>
                    Cód:{" "}
                    {codeParts.map((part: ParsedPart, index: number) => (
                      <span
                        key={`code-${index}`}
                        style={{ fontWeight: part.highlight ? 700 : 400 }}
                      >
                        {part.text}
                      </span>
                    ))}{" "}
                    • CNPJ:{" "}
                    {cnpjParts.map((part: ParsedPart, index: number) => (
                      <span
                        key={`cnpj-${index}`}
                        style={{ fontWeight: part.highlight ? 700 : 400 }}
                      >
                        {part.text}
                      </span>
                    ))}
                  </div>
                </div>
              </li>
            );
          }}
        />
        <Box display="flex" justifyContent="flex-end" style={{ gap: "15px" }}>
          <Button
            disabled={customerLoginMutation.isPending}
            onClick={handleClose}
            variant="outlined"
            color="secondary"
          >
            {isLogin ? "Efetuar Logoff" : "Cancelar"}
          </Button>
          <Button
            disabled={customerLoginMutation.isPending || !value}
            onClick={handleSelect}
            variant="contained"
            color="primary"
          >
            Ok
          </Button>
        </Box>
        {customerLoginMutation.isPending && (
          <CircularProgress
            style={{ position: "absolute", top: "50%", left: "50%" }}
          />
        )}
      </Box>
    </Modal>
  );
};

const CustomerSelect = () => {
  const clientData = getClientData();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!clientData || clientData.id_origem_pedido !== "U") {
    return null;
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ gap: "5px" }}
      >
        <IconButton onClick={handleOpen}>
          <SupervisorAccountIcon style={{ color: "white" }} />
        </IconButton>

        <Logout />
      </Box>

      <CustomerSelectModal open={open} handleClose={handleClose} />
    </>
  );
};

export { CustomerSelect, CustomerSelectModal };
